import {Segment} from 'common/query-builder/query-block/segment-menu/types';
import {Bucket} from 'common/widgets/bucket-pane/types';

export enum CohortType {
  SpecificIntervalRetained = `specific_interval_retained`,
  SpecificIntervalDropOff = `specific_interval_drop_off`,
}

export interface ErrorResponse {
  error: string;
  message?: string;
  data?: any;
  statusCode?: number;
}

export type Retention = {
  first: number;
  counts: number[];
  rates: number[];
};

export type Addiction = number[];

export type RetentionResponseMetadata = {
  min_sampling_factor?: number;
  incomplete_buckets?: {
    [date: string]: Array<null | string>;
  };
  group_by_metadata?: Array<{
    bookmark: Segment;
    numeric_buckets: Bucket;
  }>;
};

/** Wrapper for all successful responses */
type DataResponse<T> = {
  comparison_results?: T;
  comparison_metadata?: {
    incomplete_buckets?: {
      [date: string]: Array<null | string>;
    };
  };
  /** only for addiction, hack for now until we unify results and data in the api */
  data?: T;
  computed_at: string;
  results?: T /** results should only be undefined for addiction */;
  metadata: RetentionResponseMetadata;
  lift_results?: T;
};

export type KeyedByDate<T> = {
  [date: string]: T;
};

export type KeyedBySegment<T> = {
  [segment: string]: T | KeyedBySegment<T>;
};

export type RecurringResponse = KeyedByDate<Retention>;
export type SegmentedRecurringResponse = KeyedByDate<KeyedBySegment<Retention>>;

export type AddictionResponse = KeyedByDate<Addiction>;
export type SegmentedAddictionResponse = KeyedByDate<KeyedBySegment<Addiction>>;

/**
 * A dumb type-checker for when you know the type but need to convince TS.
 *
 * @param _r not checked, receives the type
 * @param segmented whether the query was segmented
 */
export function isSegmented(
  _r: RetentionData,
  segmented: boolean,
): _r is SegmentedAddictionResponse | SegmentedRecurringResponse {
  return segmented;
}
/**
 * A dumb type-checker for when you know the type but need to convince TS.
 *
 * @param _r a response object, not used except as the recipient of the type
 * @param isUnsegmented determines the outcome
 */
export function isUnsegmentedData(_r: any, isUnsegmented: boolean): _r is RecurringResponse | AddictionResponse {
  return !!isUnsegmented;
}
export function isErrorResponse(r: RetentionQueryResponse): r is ErrorResponse {
  return `error` in r;
}

/** Type for representing "unwrapped" query responses */
export type RetentionData =
  | AddictionResponse
  | SegmentedAddictionResponse
  | RecurringResponse
  | SegmentedRecurringResponse;

/** All possible query api responses */
export type RetentionQueryResponse = ErrorResponse | DataResponse<RetentionData>;
export type SuccessResponse = Exclude<RetentionQueryResponse, ErrorResponse>;

/** Maps a flattened segment back to its path, e.g. "Chrome / USA" -> ["Chrome", "USA"] */
export type SegmentKeyToPath = {[flattenedSegment: string]: string[]};
